/**
 * USMapBackdrop — editorial dotted USA map with pulsing clinic locations.
 *
 * The map itself is rendered as gold micro-dots clipped to a simplified
 * US outline. A larger set of pulsing gold markers sits on top at
 * approximate city coordinates for each TheraTec clinic install.
 *
 * viewBox: 1000 x 560  (roughly matches lower-48 aspect ratio)
 */

// Simplified USA outline path (approximate — editorial, not survey-grade).
const US_PATH = "M 178 178 L 260 158 L 348 148 L 442 140 L 540 135 L 634 132 L 728 138 L 802 150 L 858 170 L 895 200 L 908 240 L 900 280 L 878 315 L 862 350 L 872 388 L 895 420 L 895 452 L 872 478 L 838 490 L 802 488 L 782 500 L 762 530 L 738 542 L 720 528 L 712 505 L 692 488 L 658 480 L 620 482 L 588 470 L 552 450 L 520 438 L 488 428 L 455 420 L 420 425 L 385 428 L 348 420 L 312 402 L 278 378 L 248 348 L 218 308 L 195 262 L 180 215 Z";

// Clinic markers — approximate SVG coordinates matching the US outline above.
const CLINICS = [
  { city: "Seattle",       xy: [225, 175] },
  { city: "San Francisco", xy: [200, 285] },
  { city: "Los Angeles",   xy: [230, 340] },
  { city: "Beverly Hills", xy: [235, 348], featured: true, name: "The Longevity Studio" },
  { city: "Phoenix",       xy: [305, 360] },
  { city: "Denver",        xy: [402, 288] },
  { city: "Aspen",         xy: [388, 300], featured: true, name: "Maison Bennington" },
  { city: "Austin",        xy: [502, 400], featured: true, name: "Field House Wellness" },
  { city: "Dallas",        xy: [512, 388] },
  { city: "Nashville",     xy: [640, 322] },
  { city: "Chicago",       xy: [592, 240] },
  { city: "Atlanta",       xy: [682, 372] },
  { city: "Miami",         xy: [770, 470], featured: true, name: "Rive Aesthetics" },
  { city: "Orlando",       xy: [758, 452], hq: true, name: "TheraTec HQ" },
  { city: "New York",      xy: [812, 235], featured: true, name: "Élan Regenerative" },
  { city: "Boston",        xy: [852, 210] },
];

// Generate a dense grid of micro-dots that we'll clip to the US shape.
const GRID = [];
for (let y = 130; y <= 550; y += 11) {
  for (let x = 165; x <= 910; x += 11) {
    // Offset alternate rows for organic stipple look
    const ox = (y % 22 === 0) ? 0 : 5.5;
    GRID.push([x + ox, y]);
  }
}

export default function USMapBackdrop({ opacity = 1 }) {
  return (
    <div
      aria-hidden="true"
      data-testid="us-map-backdrop"
      className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
      style={{ opacity }}
    >
      <svg
        viewBox="0 0 1000 580"
        className="w-full h-full max-w-[1500px]"
        preserveAspectRatio="xMidYMid meet"
        fill="none"
      >
        <defs>
          <clipPath id="tt-us-clip">
            <path d={US_PATH} />
          </clipPath>
          <radialGradient id="tt-clinic-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#E8D6A8" stopOpacity="1" />
            <stop offset="50%" stopColor="#C9A961" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#C9A961" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Soft outline halo behind the stipple map */}
        <path
          d={US_PATH}
          stroke="#C9A961"
          strokeWidth="1.2"
          strokeOpacity="0.28"
          strokeLinejoin="round"
          fill="rgba(201,169,97,0.03)"
        />

        {/* Stippled interior — micro gold dots clipped to US shape */}
        <g clipPath="url(#tt-us-clip)">
          {GRID.map(([x, y], i) => (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={1.2}
              fill="#C9A961"
              opacity={0.35 + ((i * 37) % 30) / 100} // slight variation for texture
            />
          ))}
        </g>

        {/* Clinic markers */}
        {CLINICS.map((c, i) => {
          const [cx, cy] = c.xy;
          const size = c.hq ? 8 : c.featured ? 6 : 4;
          return (
            <g key={c.city} data-testid={`clinic-marker-${c.city.toLowerCase().replace(/\s+/g, '-')}`}>
              {/* Glow disc */}
              <circle
                cx={cx}
                cy={cy}
                r={size * 4}
                fill="url(#tt-clinic-glow)"
                opacity={c.hq ? 0.9 : c.featured ? 0.75 : 0.5}
              />
              {/* Outer pulsing ring */}
              <circle
                cx={cx}
                cy={cy}
                r={size + 3}
                stroke="#C9A961"
                strokeWidth="0.9"
                fill="none"
                opacity="0.55"
                style={{
                  transformOrigin: `${cx}px ${cy}px`,
                  animation: `tt-clinic-pulse 3.2s ease-out ${(i * 0.28) % 3}s infinite`,
                }}
              />
              {/* Core dot */}
              <circle
                cx={cx}
                cy={cy}
                r={size}
                fill={c.hq ? "#E8D6A8" : "#C9A961"}
                stroke={c.hq ? "#1C1C1C" : "none"}
                strokeWidth={c.hq ? 1 : 0}
              />
              {c.hq && (
                <circle
                  cx={cx}
                  cy={cy}
                  r={size - 3}
                  fill="#1C1C1C"
                />
              )}
            </g>
          );
        })}

        {/* Optional connecting lines between featured clinics (subtle web) */}
        <g opacity="0.18">
          {CLINICS.filter((c) => c.featured || c.hq).map((c, i, arr) => {
            const next = arr[(i + 1) % arr.length];
            return (
              <line
                key={c.city}
                x1={c.xy[0]}
                y1={c.xy[1]}
                x2={next.xy[0]}
                y2={next.xy[1]}
                stroke="#C9A961"
                strokeWidth="0.5"
                strokeDasharray="2 4"
              />
            );
          })}
        </g>
      </svg>
    </div>
  );
}
